import {
  type InvitationAcceptInput,
  InvitationAcceptInputSchema,
  type InvitationAcceptResponse,
  InvitationAcceptResponseSchema,
  type InvitationCreateInput,
  InvitationCreateInputSchema,
  type InvitationCreateResponse,
  InvitationCreateResponseSchema,
  type InvitationListResponse,
  InvitationListResponseSchema,
  type InvitationRevokeInput,
  InvitationRevokeInputSchema,
  type InvitationRevokeResponse,
  InvitationRevokeResponseSchema,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export class InvitationsApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'InvitationsApiError';
    this.code = code;
    this.status = status;
  }
}

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
  };
};

export const invitationKeys = {
  all: ['invitations'] as const,
  list: (tenantId?: string) => [...invitationKeys.all, 'list', tenantId ?? 'active'] as const,
} as const;

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function readError(res: Response): Promise<InvitationsApiError> {
  const body = (await res.json().catch(() => null)) as ErrorEnvelope | null;
  return new InvitationsApiError(
    body?.error?.code ?? 'invitation_failed',
    body?.error?.message ?? res.statusText,
    res.status,
  );
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  parse: (body: unknown) => T,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });

  if (!res.ok) {
    throw await readError(res);
  }

  return parse(await res.json());
}

export function listInvitations(supabase: SupabaseClientLike): Promise<InvitationListResponse> {
  return apiFetch(supabase, 'invitations', (body) => InvitationListResponseSchema.parse(body));
}

export function createInvitation(
  supabase: SupabaseClientLike,
  input: InvitationCreateInput,
): Promise<InvitationCreateResponse> {
  const payload = InvitationCreateInputSchema.parse(input);
  return apiFetch(supabase, 'invitations', (body) => InvitationCreateResponseSchema.parse(body), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function revokeInvitation(
  supabase: SupabaseClientLike,
  input: InvitationRevokeInput,
): Promise<InvitationRevokeResponse> {
  const payload = InvitationRevokeInputSchema.parse(input);
  return apiFetch(
    supabase,
    `invitations/${payload.id}/revoke`,
    (body) => InvitationRevokeResponseSchema.parse(body),
    { method: 'POST' },
  );
}

export function acceptInvitation(
  supabase: SupabaseClientLike,
  input: InvitationAcceptInput,
): Promise<InvitationAcceptResponse> {
  const payload = InvitationAcceptInputSchema.parse(input);
  return apiFetch(
    supabase,
    'invitations/accept',
    (body) => InvitationAcceptResponseSchema.parse(body),
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function useInvitations(supabase: SupabaseClientLike, tenantId?: string) {
  return useQuery({
    queryKey: invitationKeys.list(tenantId),
    queryFn: () => listInvitations(supabase),
    enabled: tenantId === undefined || Boolean(tenantId),
  });
}

export function useCreateInvitation(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationCreateInput) => createInvitation(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

export function useRevokeInvitation(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationRevokeInput) => revokeInvitation(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

export function useAcceptInvitation(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationAcceptInput) => acceptInvitation(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}
